import * as Yup from 'yup';
import { ClipboardEvent } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'hooks/use-auth';
import { useLogin } from 'hooks/use-login';
import { toast } from 'react-toastify';
import { SESSION_EVENT } from 'utils/constants';
import { Event } from 'utils/helpers';

export const useLoginHook = () => {
  const { t } = useTranslation();
  const { mutate: loginSession, isLoading } = useLogin();

  const {
    session,
    isLoginOpen,
    setAuthSession,
    handleIsOpenLoginModal,
    handleIsOpenLoginByEmailModal,
    handleIsOpenCheckOTPCodeOpen,
    handleChangeStartedLoginByEmailOrSMS,
  } = useAuth();

  const schema = Yup.object().shape({
    country: Yup.string().trim().required(t('@Login.country-required')),
    ddd: Yup.string().trim().required(t('@Login.ddd-required')),
    phone: Yup.string().trim().required(t('@Login.phone-required')),
  });

  const proceedToLoginBySMS = (mobile: string) => {
    handleChangeStartedLoginByEmailOrSMS(SESSION_EVENT.LOGIN_BY_SMS);
    Event.push('getin_login_telephone_click');

    loginSession(
      {
        currentFlow: 'sms',
        params: {
          mobile,
        },
      },
      {
        onSuccess: ({ data }) => {
          setAuthSession({
            ...data,
            phone: mobile,
            registerSession: session?.registerSession
              ? session?.registerSession
              : data.session,
          });
          handleIsOpenCheckOTPCodeOpen(true);
          handleIsOpenLoginModal(false);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          toast.error(
            error?.response?.data?.errors?.general ||
              'Erro ao enviar os dados. Por favor, tente novamente.'
          );
        },
      }
    );
  };

  const { values, setFieldValue, handleChange, handleSubmit, errors, touched } =
    useFormik({
      initialValues: {
        phone: '',
        country: '+55',
        ddd: '',
      },
      validationSchema: schema,
      onSubmit: ({ country, ddd, phone }) => {
        const countryFormatted = country?.replace(/[^\w\s]/gi, '');
        const phoneFormatted = phone?.replace(/[^\w\s]/gi, '');
        const mobile = `${countryFormatted}${ddd}${phoneFormatted}`;

        proceedToLoginBySMS(mobile);
      },
    });

  const formikProps = (field: keyof typeof values) => ({
    value: values[field],
    error: touched[field] && errors[field] ? errors[field] : '',
    onChange: handleChange(field),
    onPaste: (event: ClipboardEvent<HTMLInputElement>) =>
      setFieldValue(field, event.clipboardData.getData('Text')),
  });

  const handleContinueByEmail = () => {
    handleIsOpenLoginByEmailModal(true);
    handleIsOpenLoginModal(false);
  };

  return {
    isLoginOpen,
    isLoading,
    formikProps,
    handleContinueByEmail,
    handleIsOpenLoginModal,
    proceedToLoginBySMS,
    handleSubmit,
    t,
  };
};
